import type { CSSProperties, FC } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface PhotoProps {
    src: string | null
    alt: string
    sizes: string
    className?: string
    imageClassName?: string
    position?: string
    preload?: boolean
    reveal?: boolean
    fallback?: string
    style?: CSSProperties
}

// A graded photograph that fills its frame. Give the frame its size or aspect
// ratio through className. Images uploaded in the CMS live on the storage CDN
// and are served as they are; local images are resized automatically.
export const Photo: FC<PhotoProps> = ({
    src,
    alt,
    sizes,
    className,
    imageClassName,
    position,
    preload = false,
    reveal = true,
    fallback,
    style
}) => {
    if (!src) {
        return (
            <div className={cn('relative flex items-center justify-center overflow-hidden bg-flour-deep', className)} style={style} role='img' aria-label={alt}>
                <span className='px-6 text-center font-heading text-2xl italic leading-tight text-crust-soft'>
                    {fallback ?? alt}
                </span>
            </div>
        )
    }

    return (
        <div className={cn('relative overflow-hidden bg-flour-deep', className)} data-reveal={reveal ? 'image' : undefined} style={style}>
            <div className='absolute inset-0' data-settle=''>
                <Image
                    src={src}
                    alt={alt}
                    fill
                    sizes={sizes}
                    preload={preload}
                    fetchPriority={preload ? 'high' : undefined}
                    unoptimized={/^https?:\/\//.test(src)}
                    className={cn('photo-grade object-cover', imageClassName)}
                    style={position ? { objectPosition: position } : undefined}
                />
            </div>
        </div>
    )
}
